// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/** All known main dashboard widget IDs. */
export const DASHBOARD_WIDGET_IDS = [
    'compliance_score',
    'rights_requests',
    'incidents',
    'drift_approvals',
    'monitoring',
    'coverage',
    'task_summary',
    'upcoming_tasks',
    'overdue_alerts',
    'notifications',
    'filing_lifecycle',
    'department_consolidation',
    'vendor_risk',
    'discovery_classification',
    'ai_governance',
    'breach_response',
    'audit_readiness',
    'my_tasks',
    'my_section_status',
    'revision_requests',
    // Sprint 43.5.6 Epic B — new Light-tier widgets. Fill the dashboard
    // real-estate freed by the Standard/Enterprise widgets hidden on
    // Light, so a Light-package demo still feels content-rich.
    'consent_summary',
    'transfer_assessments',
    'breach_notifications',
    'authority_requests',
    'data_subjects',
    // Sprint 45.1.0 — AUDIT_TRAIL + ONBOARDING Light-tier widgets.
    'audit_activity_summary',
    'onboarding_progress',
    // Sprint 46.2.0 — Sector Overlay Reports Due. Self-hides when no
    // direct-mode sector overlays are installed; surfaces on banking/etc.
    // tenants only.
    'sector_overlay_reports_due',
];
/** Default column span for each widget in a 2-column grid layout. */
export const WIDGET_DEFAULT_COL_SPAN = {
    compliance_score: 1,
    rights_requests: 2,
    incidents: 1,
    drift_approvals: 1,
    monitoring: 1,
    coverage: 1,
    task_summary: 1,
    upcoming_tasks: 1,
    overdue_alerts: 1,
    notifications: 1,
    filing_lifecycle: 1,
    department_consolidation: 1,
    vendor_risk: 1,
    discovery_classification: 1,
    ai_governance: 1,
    breach_response: 1,
    audit_readiness: 1,
    my_tasks: 1,
    my_section_status: 1,
    revision_requests: 1,
    // Sprint 43.5.6 Epic B — new Light-tier widgets (all single-col).
    consent_summary: 1,
    transfer_assessments: 1,
    breach_notifications: 1,
    authority_requests: 1,
    data_subjects: 1,
    // Sprint 45.1.0 — AUDIT_TRAIL + ONBOARDING Light-tier widgets.
    audit_activity_summary: 1,
    onboarding_progress: 1,
    sector_overlay_reports_due: 2,
};
/** Maps each widget to its parent section. */
export const WIDGET_SECTION_MAP = {
    compliance_score: 'core_metrics',
    rights_requests: 'core_metrics',
    incidents: 'core_metrics',
    drift_approvals: 'core_metrics',
    monitoring: 'core_metrics',
    coverage: 'core_metrics',
    task_summary: 'task_management',
    upcoming_tasks: 'task_management',
    overdue_alerts: 'task_management',
    notifications: 'task_management',
    my_tasks: 'task_management',
    my_section_status: 'task_management',
    revision_requests: 'task_management',
    filing_lifecycle: 'compliance_insights',
    department_consolidation: 'compliance_insights',
    vendor_risk: 'compliance_insights',
    discovery_classification: 'compliance_insights',
    ai_governance: 'compliance_insights',
    breach_response: 'compliance_insights',
    audit_readiness: 'compliance_insights',
    // Sprint 43.5.6 Epic B — new Light-tier widgets.
    consent_summary: 'core_metrics',
    data_subjects: 'core_metrics',
    transfer_assessments: 'compliance_insights',
    breach_notifications: 'compliance_insights',
    authority_requests: 'compliance_insights',
    // Sprint 45.1.0 — AUDIT_TRAIL is an insight; ONBOARDING is a task-management nudge.
    audit_activity_summary: 'compliance_insights',
    onboarding_progress: 'task_management',
    sector_overlay_reports_due: 'compliance_insights',
};
/**
 * Sprint 43.5.5 — Module dependencies for each dashboard widget.
 * Sprint 43.5.6 — refined to hide-vs-degrade split. Widgets listed with
 * an empty array always render ("degrade gracefully" on Light-tier data
 * from whatever modules ARE installed). Widgets listed with one or more
 * modules hide entirely if ANY of those modules is disabled.
 *
 * Used by `DashboardPage` to hide widgets whose underlying module is not
 * enabled for the org's package tier. Required so a Light-package demo
 * doesn't show widgets for modules that aren't installed (e.g. Compliance
 * Score is a Governance feature and disappears from the grid when
 * Governance is disabled).
 *
 * Module IDs are intentionally typed as `string` (not `ModuleId`) to
 * avoid a type-level circular import between `dashboard.ts` and
 * `module-config.ts`. The values are verified against `ModuleId` at
 * runtime by the gating hook.
 */
export const WIDGET_MODULE_DEPENDENCIES = {
    // Core metrics
    compliance_score: ['governance'], // the Compliance Score product IS Governance
    rights_requests: ['rights_requests'],
    incidents: ['incidents'],
    drift_approvals: ['governance'],
    monitoring: ['monitoring'],
    coverage: [], // DEGRADE: shows whatever Light modules contribute
    // Task management
    // Sprint 45.2.0 — task_summary/upcoming_tasks/my_tasks flipped from
    // DEGRADE to HIDE. The widgets' "View all tasks" CTAs route into
    // /admin/tasks/*, which is RBAC-gated on Task Management permissions.
    // On Light (Task Management is Standard+), the widgets rendered
    // happily but every click-through hit 403. Gating on task_management
    // removes the broken CTAs from the Light dashboard entirely.
    task_summary: ['task_management'],
    upcoming_tasks: ['task_management'],
    overdue_alerts: ['task_management'], // HIDE: per product, overdue alerts only source from Task Management
    notifications: [], // core notification system; no module gate (licenses, MFA, SSO — org-wide infrastructure)
    my_tasks: ['task_management'],
    my_section_status: ['program_governance'],
    revision_requests: ['program_governance'],
    // Compliance insights
    filing_lifecycle: ['assessments'],
    department_consolidation: ['program_governance'],
    vendor_risk: ['vendor_risk'],
    discovery_classification: ['discovery', 'classification'],
    ai_governance: ['classification', 'discovery'],
    breach_response: ['incidents'], // degrades on Light; incident_ops is enrichment only
    audit_readiness: ['assessments'], // degrades on Light; program_governance is enrichment only
    // Sprint 43.5.6 Epic B — new Light-tier widgets.
    consent_summary: ['consent'],
    transfer_assessments: ['assessments'],
    breach_notifications: ['incidents'],
    authority_requests: ['compliance_forms'],
    data_subjects: ['data_subjects'],
    // Sprint 45.1.0 — AUDIT_TRAIL + ONBOARDING widget module gates.
    audit_activity_summary: ['audit_trail'],
    onboarding_progress: [], // DEGRADE: wizard state is org-level, always available
    // Widget self-hides when no direct-mode sector overlays are installed
    // (data-state gate, stronger than module-availability), so no module dep.
    sector_overlay_reports_due: [],
};
/* ── Sprint 22.7.2: Drag-and-Drop Widget Reordering ── */
/**
 * Returns the default widget order for a given section based on DASHBOARD_WIDGET_IDS.
 */
export function getDefaultSectionOrder(sectionId) {
    return DASHBOARD_WIDGET_IDS.filter((id) => WIDGET_SECTION_MAP[id] === sectionId);
}
/**
 * Default widget configurations per persona.
 * First-visit users see only the widgets relevant to their detected persona.
 * If no persona detected, falls back to all-visible (existing behavior).
 */
export const PERSONA_WIDGET_DEFAULTS = {
    // P-VN-01: Enterprise DPO — compliance, rights, incidents, filing, audit
    'P-VN-01': {
        visible: [
            'compliance_score',
            'rights_requests',
            'incidents',
            'drift_approvals',
            'monitoring',
            'coverage',
            'task_summary',
            'upcoming_tasks',
            'overdue_alerts',
            'filing_lifecycle',
            'audit_readiness',
            'my_tasks',
            // Sprint 43.5.6 Epic B — new Light-tier widgets
            'consent_summary',
            'data_subjects',
            'transfer_assessments',
            'breach_notifications',
            'authority_requests',
            'sector_overlay_reports_due',
        ],
        collapsed: ['coverage', 'monitoring'],
    },
    // P-VN-02: SME Privacy Officer — streamlined compliance focus
    'P-VN-02': {
        visible: [
            'compliance_score',
            'rights_requests',
            'incidents',
            'task_summary',
            'upcoming_tasks',
            'overdue_alerts',
            'notifications',
            'filing_lifecycle',
            'my_tasks',
            // Sprint 43.5.6 Epic B — new Light-tier widgets
            'consent_summary',
            'data_subjects',
            'transfer_assessments',
            'breach_notifications',
            'authority_requests',
            // Sprint 45.1.0 — AUDIT_TRAIL + ONBOARDING widgets for the Light
            // persona so a fresh install surfaces what's configured so far and
            // nudges completion of the setup wizard.
            'audit_activity_summary',
            'onboarding_progress',
        ],
        collapsed: [],
    },
    // P-VN-03: Compliance Program Manager — program governance, monitoring
    'P-VN-03': {
        visible: [
            'compliance_score',
            'monitoring',
            'coverage',
            'task_summary',
            'upcoming_tasks',
            'overdue_alerts',
            'filing_lifecycle',
            'department_consolidation',
            'audit_readiness',
            'my_tasks',
            // Sprint 43.5.6 Epic B — new Light-tier widgets
            'consent_summary',
            'transfer_assessments',
            'breach_notifications',
            'authority_requests',
        ],
        collapsed: [],
    },
    // P-VN-04: Department Data Owner — department tasks, section status
    'P-VN-04': {
        visible: [
            'task_summary',
            'upcoming_tasks',
            'overdue_alerts',
            'notifications',
            'my_tasks',
            'my_section_status',
            'revision_requests',
        ],
        collapsed: [],
    },
    // P-VN-05: Data Governance Lead — discovery, classification, governance
    'P-VN-05': {
        visible: [
            'compliance_score',
            'coverage',
            'task_summary',
            'upcoming_tasks',
            'overdue_alerts',
            'discovery_classification',
            'vendor_risk',
            'department_consolidation',
            'my_tasks',
        ],
        collapsed: [],
    },
    // P-VN-06: Security Operations Lead — incidents, breach, drift
    'P-VN-06': {
        visible: [
            'compliance_score',
            'incidents',
            'drift_approvals',
            'monitoring',
            'task_summary',
            'upcoming_tasks',
            'overdue_alerts',
            'breach_response',
            'my_tasks',
            'sector_overlay_reports_due',
        ],
        collapsed: [],
    },
    // P-VN-07: IT Systems Administrator — coverage, connectors, monitoring
    'P-VN-07': {
        visible: [
            'monitoring',
            'coverage',
            'task_summary',
            'upcoming_tasks',
            'overdue_alerts',
            'notifications',
            'discovery_classification',
            'my_tasks',
        ],
        collapsed: [],
    },
    // P-VN-08: Legal Counsel — compliance, filings, AI governance
    'P-VN-08': {
        visible: [
            'compliance_score',
            'rights_requests',
            'incidents',
            'task_summary',
            'upcoming_tasks',
            'filing_lifecycle',
            'ai_governance',
            'audit_readiness',
            'my_tasks',
            'sector_overlay_reports_due',
        ],
        collapsed: [],
    },
    // P-VN-09: Breach Response Lead — incident + breach workflows (Sprint 43.5.0 E10 — persona label fix)
    // Previously miscommented as "Executive / Board Member"; persona registry
    // `docs/content/personas-and-usecases.md` defines P-VN-09 as Breach Response Lead.
    // Widget bundle revised to surface the queues this persona works from daily.
    'P-VN-09': {
        visible: [
            'incidents',
            'breach_response',
            'drift_approvals',
            'filing_lifecycle',
            'audit_readiness',
            'task_summary',
            'overdue_alerts',
            'my_tasks',
        ],
        collapsed: [],
    },
    // P-VN-10: Internal Audit Lead — audit, evidence, compliance
    'P-VN-10': {
        visible: [
            'compliance_score',
            'coverage',
            'task_summary',
            'upcoming_tasks',
            'overdue_alerts',
            'filing_lifecycle',
            'audit_readiness',
            'department_consolidation',
            'my_tasks',
            'sector_overlay_reports_due',
        ],
        collapsed: [],
    },
    // P-VN-11: HR & Employee Data Steward — consent, rights, tasks
    'P-VN-11': {
        visible: [
            'compliance_score',
            'rights_requests',
            'task_summary',
            'upcoming_tasks',
            'overdue_alerts',
            'notifications',
            'coverage',
            'my_tasks',
        ],
        collapsed: [],
    },
    // P-VN-12: Multinational Subsidiary Compliance Lead — assessments, rights, localization
    'P-VN-12': {
        visible: [
            'compliance_score',
            'rights_requests',
            'incidents',
            'drift_approvals',
            'task_summary',
            'upcoming_tasks',
            'overdue_alerts',
            'filing_lifecycle',
            'audit_readiness',
            'my_tasks',
        ],
        collapsed: ['drift_approvals'],
    },
    // P-VN-13: Education Compliance Lead — consent, rights, monitoring
    'P-VN-13': {
        visible: [
            'compliance_score',
            'rights_requests',
            'monitoring',
            'task_summary',
            'upcoming_tasks',
            'overdue_alerts',
            'coverage',
            'notifications',
            'my_tasks',
        ],
        collapsed: [],
    },
    // P-VN-14: External Legal Advisor — multi-client portfolio, filing, audit
    'P-VN-14': {
        visible: [
            'compliance_score',
            'task_summary',
            'upcoming_tasks',
            'overdue_alerts',
            'filing_lifecycle',
            'audit_readiness',
            'my_tasks',
        ],
        collapsed: [],
    },
    // P-VN-15: Law Firm Operations Manager — tasks, deliverables, deadlines
    'P-VN-15': {
        visible: ['task_summary', 'upcoming_tasks', 'overdue_alerts', 'notifications', 'my_tasks'],
        collapsed: [],
    },
    // P-VN-16: Business Owner / CEO — executive dashboard (Sprint 43.5.0 E10 — bundle expansion)
    // Extended from 5 to 8 widgets so Scene 7 of the VN Decree 356 demo close
    // renders the full compliance picture (filing completeness, active rights
    // requests, incident posture) that a CEO/board audience expects.
    'P-VN-16': {
        visible: [
            'compliance_score',
            'filing_lifecycle',
            'incidents',
            'rights_requests',
            'audit_readiness',
            'monitoring',
            'overdue_alerts',
            'notifications',
        ],
        collapsed: ['monitoring'],
    },
};
//# sourceMappingURL=dashboard.js.map